import {FC, useEffect, useState, ChangeEvent} from 'react'
import {api, MaintenanceRequest, RequestStatusValue, RequestTypeValue, ApiError} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {confirmDialog} from '../../components/dialog'
import {RequestChat} from '../../components/RequestChat'
import {useIsMobile} from '../../hooks/useIsMobile'
import {PageHead} from './ui'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const STATUS_TONE: Record<RequestStatusValue, {color: string; bg: string; label: string}> = {
  OPEN: {color: '#b8770a', bg: 'rgba(255,168,0,0.15)', label: 'Open'},
  IN_PROGRESS: {color: '#2b4eff', bg: 'rgba(43,78,255,0.12)', label: 'In progress'},
  RESOLVED: {color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)', label: 'Resolved'},
  CLOSED: {color: '#7e8299', bg: 'rgba(126,130,153,0.15)', label: 'Closed'},
}
const TYPE_LABEL: Record<RequestTypeValue, string> = {CLEANING: '🧹 Cleaning', MAINTENANCE: '🔧 Maintenance', SUGGESTION: '💡 Suggestion'}

const Pill: FC<{status: RequestStatusValue}> = ({status}) => {
  const t = STATUS_TONE[status]
  return <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '4px 10px', color: t.color, background: t.bg}}>{t.label}</span>
}

const EyeIcon: FC = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z' />
    <circle cx='12' cy='12' r='3' />
  </svg>
)

const fmtDateTime = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {dateStyle: 'medium', timeStyle: 'short'})
}

const inp: React.CSSProperties = {width: '100%', padding: '11px 13px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 14, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}

const RequestsPage: FC = () => {
  const toast = useToast()
  const [requests, setRequests] = useState<MaintenanceRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)

  const [type, setType] = useState<RequestTypeValue>('CLEANING')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [photo, setPhoto] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [selected, setSelected] = useState<MaintenanceRequest | null>(null)
  const isMobile = useIsMobile()
  const [showForm, setShowForm] = useState(false)

  const [fee, setFee] = useState('5.00')

  const load = () => api.myRequests().then(setRequests).catch(() => setRequests([])).finally(() => setLoading(false))
  useEffect(() => {
    void load()
    api.paymentMethods().then((m) => setFee(Number(m.bedWashFee).toFixed(2))).catch(() => {})
  }, [])

  const submit = async () => {
    if (title.trim().length < 3) return toast.error('Add a short subject (min 3 chars).')
    if (description.trim().length < 5) return toast.error('Describe the issue (min 5 chars).')
    setSubmitting(true)
    try {
      const attachmentKey = photo ? (await api.uploadFile(photo)).key : undefined
      await api.createRequest({type, title: title.trim(), description: description.trim(), attachmentKey})
      toast.success('Request submitted — we’ll keep you posted.')
      setTitle('')
      setDescription('')
      setPhoto(null)
      setType('CLEANING')
      setShowForm(false)
      void api.myRequests().then(setRequests)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not submit your request')
    } finally {
      setSubmitting(false)
    }
  }

  // Optional $5 bed-sheet washing. Charges the card on file right away (or opens
  // Stripe Checkout to vault one), then the backend drops the job straight into
  // the cleaner's queue — no Zelle, no separate ticket to chase.
  const [washing, setWashing] = useState(false)
  const requestSheetWashing = async () => {
    const ok = await confirmDialog({
      title: 'Request a bed wash?',
      text: `We'll charge $${fee} to your saved card and housekeeping will take care of it.`,
      confirmText: `Pay $${fee}`,
    })
    if (!ok) return
    setWashing(true)
    try {
      // Log the request first so its id links the wash job — then the cleaner's
      // Start/Done updates flow back to this request's status.
      const req = await api.logBedWashRequest(fee)
      const res = await api.startBedWash(req.id)
      if ('url' in res) {
        window.location.href = res.url // no card on file → Checkout ($5 + saves card)
        return
      }
      toast.success(`Sheet washing requested — $${fee} charged. The cleaner will wash your bed.`)
      void load()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not start sheet washing. Do you have an active booking?')
    } finally {
      setWashing(false)
    }
  }

  const cancel = async (r: MaintenanceRequest) => {
    const ok = await confirmDialog({title: 'Cancel this request?', text: `"${r.title}" will be closed.`, confirmText: 'Cancel request', danger: true})
    if (!ok) return
    setBusyId(r.id)
    try {
      const updated = await api.cancelRequest(r.id)
      setRequests((list) => list.map((x) => (x.id === r.id ? updated : x)))
      toast.success('Request cancelled.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not cancel')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <PageHead
        icon='🧹'
        eyebrow='Support'
        title='Cleaning & Maintenance'
        sub='Report an issue or share a suggestion — we’ll handle it and keep you updated.'
        action={
          <div style={{display: 'flex', flexWrap: 'wrap', gap: 10}}>
            <button onClick={requestSheetWashing} disabled={washing} style={{fontWeight: 700, fontSize: 15, color: '#fff', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.45)', borderRadius: 10, padding: '12px 18px', cursor: washing ? 'not-allowed' : 'pointer', opacity: washing ? 0.7 : 1, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 8}}>
              {washing ? <Spinner /> : '🧺'} Request sheet washing ($5)
            </button>
            <button onClick={() => setShowForm(true)} className='ccp-cta-light' style={{fontWeight: 700, fontSize: 15, color: ACCENT, background: '#fff', border: 'none', borderRadius: 10, padding: '12px 22px', cursor: 'pointer', whiteSpace: 'nowrap'}}>
              + Report an issue
            </button>
          </div>
        }
      />

      {/* My requests */}
      <h2 style={{fontSize: 17, fontWeight: 800, color: INK, margin: '0 0 14px'}}>My requests</h2>
      {loading ? (
        <div style={{padding: 30, textAlign: 'center', color: MUTED}}>Loading…</div>
      ) : requests.length === 0 ? (
        <div style={{background: '#fff', borderRadius: 16, padding: 36, textAlign: 'center', color: MUTED, boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>No requests yet.</div>
      ) : (
        <div className='ccp-fade-up-2' style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', overflow: 'hidden'}}>
          {isMobile ? (
            <div>
              {requests.map((r) => (
                <div key={r.id} onClick={() => setSelected(r)} style={{borderTop: '1px solid #f1f3f7', padding: '14px 16px', cursor: 'pointer'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10}}>
                    <div style={{fontWeight: 600, color: INK, minWidth: 0}}>{r.title}</div>
                    <Pill status={r.status} />
                  </div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 6, fontSize: 13, color: MUTED}}>
                    <span>{TYPE_LABEL[r.type]}</span>
                    <span>Bed {r.bed?.code ?? '—'}</span>
                    <span>{new Date(r.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{textAlign: 'left', color: MUTED, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                  <th style={th}>Type</th>
                  <th style={th}>Subject</th>
                  <th style={th}>Bed</th>
                  <th style={th}>Status</th>
                  <th style={th}>Date</th>
                  <th style={{...th, textAlign: 'right'}}>View</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} onClick={() => setSelected(r)} className='ccp-row' style={{borderTop: '1px solid #f1f3f7', cursor: 'pointer'}}>
                    <td style={{...td, color: MUTED, whiteSpace: 'nowrap'}}>{TYPE_LABEL[r.type]}</td>
                    <td style={{...td, fontWeight: 600, color: INK}}>{r.title}</td>
                    <td style={{...td, color: MUTED}}>{r.bed?.code ?? '—'}</td>
                    <td style={td}><Pill status={r.status} /></td>
                    <td style={{...td, color: MUTED}}>{new Date(r.createdAt).toLocaleDateString()}</td>
                    <td style={{...td, textAlign: 'right'}}>
                      <button onClick={(e) => {e.stopPropagation(); setSelected(r)}} title='View request' aria-label='View request' style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: ACCENT, cursor: 'pointer'}}>
                        <EyeIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {selected && (
        <RequestDetailModal
          request={selected}
          busy={busyId === selected.id}
          onClose={() => setSelected(null)}
          onCancel={async () => {
            await cancel(selected)
            setSelected(null)
          }}
        />
      )}

      {/* New request — full-view modal */}
      {showForm && (
        <div onClick={() => !submitting && setShowForm(false)} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto'}}>
          <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, width: '100%', maxWidth: 760, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'}}>
            <div style={{background: 'linear-gradient(135deg, #039540, #026e2f)', color: '#fff', padding: '20px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <div>
                <div style={{fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85}}>New request</div>
                <div style={{fontSize: 20, fontWeight: 800, marginTop: 2}}>Report an issue</div>
              </div>
              <button onClick={() => !submitting && setShowForm(false)} aria-label='Close' style={{background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', fontSize: 19, lineHeight: 1, cursor: 'pointer'}}>×</button>
            </div>

            <div style={{padding: '24px 28px'}}>
              <div className='row g-3'>
                <div className='col-12 col-md-4'>
                  <label style={lbl}>Type</label>
                  <select value={type} onChange={(e) => setType(e.target.value as RequestTypeValue)} style={inp}>
                    <option value='CLEANING'>Cleaning</option>
                    <option value='MAINTENANCE'>Maintenance</option>
                    <option value='SUGGESTION'>Suggestion</option>
                  </select>
                </div>
                <div className='col-12 col-md-8'>
                  <label style={lbl}>Subject</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder='e.g. Heating not working' style={inp} />
                </div>
                <div className='col-12'>
                  <label style={lbl}>Description</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder='Describe the issue or idea…' style={{...inp, minHeight: 120, resize: 'vertical'}} />
                </div>
                <div className='col-12'>
                  <label style={lbl}>Photo (optional)</label>
                  <label style={{display: 'flex', alignItems: 'center', gap: 12, border: `1px dashed ${photo ? ACCENT : '#cbd2e0'}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', background: photo ? 'rgba(3,149,64,0.05)' : '#fafbfc'}}>
                    <span style={{fontSize: 20}}>{photo ? '✅' : '📷'}</span>
                    <span style={{fontSize: 14, color: photo ? INK : MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{photo ? photo.name : 'Attach a photo or PDF…'}</span>
                    <input type='file' accept='image/jpeg,image/png,image/webp,application/pdf' onChange={(e: ChangeEvent<HTMLInputElement>) => setPhoto(e.target.files?.[0] ?? null)} style={{display: 'none'}} />
                  </label>
                </div>
              </div>
            </div>

            <div style={{padding: '0 28px 24px', display: 'flex', justifyContent: 'flex-end', gap: 10}}>
              <button onClick={() => setShowForm(false)} disabled={submitting} style={{fontWeight: 600, fontSize: 15, color: INK, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 22px', cursor: 'pointer'}}>Cancel</button>
              <button onClick={submit} disabled={submitting} style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 28px', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1, minWidth: 150}}>
                {submitting ? <Spinner size={14} /> : 'Submit request'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.ccp-row:hover { background: #f8fafc; }`}</style>
    </div>
  )
}

// ─── Detail modal with full admin-note history ──────────────────────────
const RequestDetailModal: FC<{request: MaintenanceRequest; busy: boolean; onClose: () => void; onCancel: () => void}> = ({request: r, busy, onClose, onCancel}) => {
  // Admin notes from the update history (newest first), with a fallback.
  const notes = (r.updates ?? []).filter((u) => u.note)
  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, width: '100%', maxWidth: 920, minHeight: 'calc(100vh - 48px)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
        <div style={{background: 'linear-gradient(135deg, #039540, #026e2f)', color: '#fff', padding: '20px 28px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12}}>
          <div>
            <div style={{fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85}}>{TYPE_LABEL[r.type]}{r.bed ? ` · Bed ${r.bed.code}` : ''}</div>
            <div style={{fontSize: 19, fontWeight: 800, marginTop: 2}}>{r.title}</div>
          </div>
          <button onClick={onClose} aria-label='Close' style={{background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: '50%', fontSize: 18, lineHeight: 1, cursor: 'pointer', flexShrink: 0}}>×</button>
        </div>

        <div style={{padding: '18px 28px 8px', flex: 1, overflowY: 'auto'}}>
          <div style={{marginBottom: 14}}><Pill status={r.status} /></div>
          <p style={{fontSize: 14, color: '#475569', lineHeight: 1.6, margin: '0 0 18px'}}>{r.description}</p>

          <div style={{fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10}}>Admin notes</div>
          {notes.length === 0 ? (
            <p style={{fontSize: 14, color: MUTED, margin: '0 0 8px'}}>No admin notes yet.</p>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 8}}>
              {[...notes].reverse().map((u, i) => (
                <div key={i} style={{background: '#f8fafc', borderLeft: `3px solid ${STATUS_TONE[u.status].color}`, borderRadius: 8, padding: '10px 14px'}}>
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4}}>
                    <Pill status={u.status} />
                    <span style={{fontSize: 12, color: MUTED}}>{fmtDateTime(u.at)}</span>
                  </div>
                  <div style={{fontSize: 14, color: INK}}>{u.note}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{marginTop: 18, borderTop: '1px solid #f1f3f7', paddingTop: 16}}>
            <RequestChat requestId={r.id} mySide='RENTER' />
          </div>
        </div>

        <div style={{padding: '12px 24px 22px', display: 'flex', gap: 10}}>
          {r.status === 'OPEN' && !r.prepaid && (
            <button onClick={onCancel} disabled={busy} style={{flex: 1, fontWeight: 700, fontSize: 14, color: '#d9214e', background: 'rgba(217,33,78,0.1)', border: 'none', borderRadius: 10, padding: '11px 0', cursor: 'pointer'}}>
              {busy ? <Spinner size={14} /> : 'Cancel request'}
            </button>
          )}
          <button onClick={onClose} style={{flex: 1, fontWeight: 700, fontSize: 14, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '11px 0', cursor: 'pointer'}}>Close</button>
        </div>
      </div>
    </div>
  )
}

const th: React.CSSProperties = {padding: '13px 18px', fontWeight: 600}
const td: React.CSSProperties = {padding: '14px 18px', verticalAlign: 'middle'}
const lbl: React.CSSProperties = {display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}

export {RequestsPage}
